import { useParams, Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { BookingModal } from "@/components/BookingModal";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, CheckCircle2 } from "lucide-react";

const VALID_CITIES = [
  "Calgary", "Red Deer", "Lethbridge", "St. Albert", "Medicine Hat",
  "Grande Prairie", "Airdrie", "Spruce Grove", "Leduc", "Cochrane",
  "Fort McMurray", "Lloydminster", "Camrose", "Beaumont", "Sherwood Park",
  "Okotoks", "Chestermere", "Brooks", "Wetaskiwin", "Fort Saskatchewan",
  "Winterburn", "Goodridge Corners", "Alberta Park", "Rampart Industrial",
  "Ellerslie Industrial", "Acheson", "Nisku",
];

const slugify = (city: string) =>
  city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const unslugify = (slug: string) => {
  const found = VALID_CITIES.find((c) => slugify(c) === slug);
  return found || slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
};

const benefits = [
  "Never miss another customer call with instant automated text back",
  "24/7 AI receptionist to answer questions and book appointments",
  "Automated appointment reminders that reduce no-shows",
  "Capture every lead with website chat and follow-up automation",
  "Build your online reputation with automated review requests",
  "Manage all customer communication from one simple dashboard",
];

const industries = [
  "AI for HVAC Services", "AI for Plumbers", "AI for Roofers", "AI for Auto Body Shops",
  "AI for Mechanics", "AI for Realtors", "AI for Lawyers", "AI for Hair Salons",
  "AI for Dentists", "AI for Medical Clinics", "AI for Restaurants", "AI for Contractors",
  "AI for Garage Door Repair", "AI for Restoration (Water/Fire/Mold)", "AI for Personal Injury Law Firms",
  "AI for Cosmetic Dentistry", "AI for Veterinary Clinics", "AI for Property Management Companies",
  "AI for Home Care Agencies", "AI for Mortgage Brokers", "AI for Real Estate Teams",
  "AI for Auto Repair Shops", "AI for Med Spas", "AI for Tree Service Companies",
];

const CityBusiness = () => {
  const { city: citySlug } = useParams<{ city: string }>();
  const cityName = citySlug ? unslugify(citySlug) : "";

  if (!cityName || !VALID_CITIES.includes(cityName)) {
    return <Navigate to="/serviced-areas" replace />;
  }

  const slug = slugify(cityName);

  return (
    <Layout>
      <SEO
        title={`${cityName} Business Automation & AI Solutions | Missed Call Rescue`}
        description={`${cityName} small business automation and AI solutions from Missed Call Rescue. Automated text back, AI receptionist, appointment booking & workflow automation for ${cityName} businesses.`}
        canonical={`/serviced-areas/${slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Missed Call Rescue - ${cityName} Business Automation`,
          description: `AI and automation solutions for ${cityName} small businesses including missed call text back, AI receptionist, and automated appointment booking.`,
          url: `https://missedcallrescuecanada.com/serviced-areas/${slug}`,
          telephone: ["1-855-780-3522", "780-237-6933"],
          address: {
            "@type": "PostalAddress",
            addressLocality: cityName,
            addressRegion: "AB",
            addressCountry: "CA",
          },
          areaServed: `${cityName}, Alberta`,
        }}
      />
      <div className="pt-24 pb-16 md:pt-32 md:pb-24 bg-slate-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full mb-6">
              <MapPin className="h-5 w-5" />
              <span className="font-semibold">{cityName}, Alberta</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              {cityName} Business Automation & AI Solutions for Small Businesses
            </h1>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Missed Call Rescue is {cityName}'s trusted provider of{" "}
              <strong className="text-primary">AI solutions for small businesses</strong>.
              We help local {cityName} companies capture more leads, increase sales, improve customer
              communication, and automate everyday tasks so you can focus on running your business.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Whether you're a contractor, dentist, lawyer, realtor, automotive shop, medical clinic,
              salon, restaurant, or any service-based business in {cityName}, we provide the automation
              tools you need to grow faster while delivering an exceptional customer experience.
            </p>
            <BookingModal>
              <Button
                size="lg"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
              >
                Get Your Free {cityName} Consultation
              </Button>
            </BookingModal>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-8 text-center">
              Why {cityName} Businesses Choose Missed Call Rescue
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex items-start gap-3"
                >
                  <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-8 text-center">
              Industries We Serve in {cityName}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {industries.map((industry, i) => (
                <div
                  key={i}
                  className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 text-center"
                >
                  <span className="font-medium text-slate-800">{industry}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-primary mb-4">
              Ready to Grow Your {cityName} Business?
            </h2>
            <p className="text-lg text-slate-700 mb-8">
              Contact us today for a free consultation and discover how our powerful automation tools
              can help your {cityName} business attract more customers, respond faster, and grow
              without the overhead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <BookingModal>
                <Button
                  size="lg"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
                >
                  Book Your Free Consultation
                </Button>
              </BookingModal>
              <a href="tel:17802376933">
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-6 rounded-full border-primary text-primary hover:bg-primary hover:text-white transition-all"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call 780-237-6933
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CityBusiness;
